import { Injectable } from 'angular-ts-decorators';

import { map, round, find } from 'lodash';

import { DateSource } from '@shared/app/scripts/services/date-source';
import { EpsonFiscalPrinter } from '@shared/app/scripts/services/epson-fiscal-printer';
import { Printer } from '@shared/app/scripts/services/printer';
import { SaleModel } from '@shared/app/scripts/classes/sale-model';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { Translate } from '@shared/app/scripts/services/translate';

const simpleSaleLine = (line) => {
    return `<printRecItem description="${line.articleDescription}" quantity="${line.qty}" unitPrice="${line.nettPrice}" department="1" justification="1" />`;
  },
  returnSaleLine = (line) => {
    return `<printRecRefund description="${
      line.articleDescription
    }" quantity="${-line.qty}" unitPrice="${
      line.price
    }" department="1" justification="1" />`;
  },
  lineDiscountValue = (line) => {
    return round(line.qty * (line.nettPrice - line.price), 2);
  },
  priceChange = (line) => {
    if (line.price > line.nettPrice) {
      // price increase
      return `<printRecItemAdjustment adjustmentType="5" description="Surcharge${
        line.reason ? ' ' + line.reason.desc : ''
      }" amount="${-lineDiscountValue(line)}" justification="1" />`;
    }

    if (line.price < line.nettPrice) {
      // discount
      return `<printRecItemAdjustment adjustmentType="0" description="Discount${
        line.reason ? ' ' + line.reason.desc : ''
      }" amount="${lineDiscountValue(line)}" justification="1" />`;
    }
  },
  receiptLine = (line) => {
    if (line.category !== 'barcode') {
      return '';
    }

    if (line.qty > 0 && line.price === line.nettPrice) {
      return simpleSaleLine(line);
    }

    if (line.qty > 0 && line.price !== line.nettPrice) {
      return `${simpleSaleLine(line)}
${priceChange(line)}`;
    }

    if (line.qty < 0) {
      return returnSaleLine(line);
    }

    return '';
  };

const paymentMethod = (payment, amount) => {
  if (amount > 0) {
    let paymentType = 3; // 'ticket' payment type (coupons?)

    switch (payment) {
      case 'cash':
        paymentType = 1;
        break;
      case 'pin':
      case 'creditcard':
      case 'creditreceipt':
        paymentType = 2;
        break;
    }

    return `<printRecTotal description="${payment}" payment="${amount}" paymentType="${paymentType}" index="1" justification="1" />`;
  }

  if (amount < 0) {
    let paymentType = 3; // 'ticket' payment type (coupons?)

    switch (payment) {
      case 'cash':
        paymentType = 1;
        break;
      case 'pin':
      case 'creditcard':
      case 'creditreceipt':
        paymentType = 2;
        break;
    }

    return `<printRecTotal description="Refund ${payment}" payment="${-amount}" paymentType="${paymentType}" index="1" justification="1" />`;
  }

  return '';
};

const discount = (discount, description: string) => {
  if (discount > 0) {
    return `<printRecSubtotalAdjustment adjustmentType="1" description="${description}" amount="${discount}" justification="1" />`;
  }

  return '';
};

const couponPayment = (discount) => {
  if (discount > 0) {
    return `<printRecTotal description="Coupon" payment="${discount}" paymentType="6" index="0" justification="1" />`;
  }

  return '';
};

/**
 * Desing to be considered:
 *
 * FiscalPrinter service that expose methods:
 * * receipt
 * * transit
 * * coupons
 * etc. similar to LegacyPrinter in v6
 *
 * This service reads settings & calls relevant templating engine:
 * * ItalianFiscalTemplate.[receipt|transit|...]
 * * TextTemplate...
 * * HtmlTemplate...
 *
 * And then sends it to relevant printer connection (?):
 * UsbPrinter
 * BrowserPrinter
 */

/**
 * Service that accepts data models, and pass them to the right printer mechanizm.
 */
@Injectable('fiscalPrinter')
export class FiscalPrinter {
  private $translate: Translate;
  private parser = new DOMParser();

  constructor(
    translate: Translate,
    private $q: ng.IQService,
    private dateSource: DateSource,
    private epsonFiscalPrinter: EpsonFiscalPrinter,
    private printer: Printer,
    private sentry: Sentry,
  ) {
    'ngInject';

    this.$translate = translate;
  }

  private processErrorXml(errorXml) {
    const error = Error('Got error from printer'),
      code = errorXml.documentElement
        .getElementsByTagName('response')[0]
        ?.getAttribute('code');

    this.sentry.captureException(error, (scope) => {
      return scope;
    });

    return this.$q.reject({
      code,
      message: this.$translate.instant('shared.fiscal_printer_error_reponse', {
        code,
      }),
    });
  }

  private processErrorString(errorString) {
    const error = Error('Cannot parse the epsonFiscalPrinter response');

    this.sentry.captureException(error, (scope) => {
      scope.setExtra('body', errorString);
      scope.setExtra('response.data', errorString);

      return scope;
    });

    return this.$q.reject({
      message: this.$translate.instant(
        'shared.fiscal_printer_error_unparseable',
      ),
    });
  }

  /**
   * Print receipt
   * @param transaction object to be printed
   * @return resolve to true if succesfull.
   */
  receipt(sale: SaleModel): ng.IPromise<any> {
    // validate the transaction
    const hasReturn = find(sale.receipt, (line) => line.qty < 0);

    // 1. Is sales or returns only
    if (hasReturn) {
      return this.$q.reject('Fiscal receipt cannot combine returns and sales');
    }

    const header = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerFiscalReceipt>`;

    const footer = `<endFiscalReceipt />
</printerFiscalReceipt>
</s:Body>
</s:Envelope>`;

    const printout = `${header}
<beginFiscalReceipt />
${map(sale?.receipt, receiptLine).join('\n')}
${discount(sale.couponDiscount, 'Coupon discount')}
${discount(sale.overallReceiptDiscount, 'Total discount')}
${couponPayment(0)}
${paymentMethod('cash', sale.payments?.cash)}
${paymentMethod('pin', sale.payments?.pin)}
${footer}`;

    return this.epsonFiscalPrinter.print(printout).then(
      (response) => {
        if (angular.isString(response.data)) {
          const parsedResponse = this.parser.parseFromString(
            response.data,
            'text/xml',
          );

          const fiscalReceiptNumber =
              parsedResponse.documentElement.getElementsByTagName(
                'fiscalReceiptNumber',
              )[0]?.textContent,
            fiscalShiftNumber =
              parsedResponse.documentElement.getElementsByTagName(
                'zRepNumber',
              )[0]?.textContent,
            fiscalReceiptDate =
              parsedResponse.documentElement.getElementsByTagName(
                'fiscalReceiptDate',
              )[0]?.textContent;

          let fiscalDate: string;

          if (
            fiscalReceiptDate &&
            angular.isString(fiscalReceiptDate) &&
            fiscalReceiptDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)
          ) {
            // assume d/m/yyyy format from the printer
            const [day, month, year] = fiscalReceiptDate.split('/');

            fiscalDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
              2,
              '0',
            )}`;
          } else {
            const now = this.dateSource.now();

            fiscalDate = now.toISOString().substr(0, 10);
          }

          if (fiscalReceiptNumber) {
            return { fiscalReceiptNumber, fiscalShiftNumber, fiscalDate };
          }

          return this.processErrorXml(parsedResponse);
        }

        return this.processErrorString(response.data);
      },
      () => {
        return this.$q.reject({
          message: this.$translate.instant(
            'shared.fiscal_printer_request_failed',
          ),
        });
      },
    );
  }

  /**
   * Print return receipt
   * @param transaction object to be printed
   * @param transaction object to referenced
   * @return resolve to true if succesfull.
   */
  // TODO cover total and discount coupons with test
  return(sale: SaleModel, originalSale: SaleModel): ng.IPromise<any> {
    // validate the transaction
    const hasSale = find(sale.receipt, (line) => line.qty > 0);

    // 1. Is sales or returns only
    if (hasSale) {
      return this.$q.reject('Fiscal receipt cannot combine returns and sales');
    }

    // 2. It has fiscal receipt
    if (!originalSale.fiscal_receipt_nr) {
      return this.$q.reject('Fiscal returns needs to be printed first');
    }

    // 3. It all fiscal fields
    if (
      !originalSale.fiscal_shift_nr ||
      !originalSale.fiscal_printer_id ||
      !originalSale.fiscal_shift_nr
    ) {
      return this.$q.reject('Fiscal returns needs to be printed first');
    }

    const header = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerFiscalReceipt>`;

    const footer = `<endFiscalReceipt />
</printerFiscalReceipt>
</s:Body>
</s:Envelope>`;

    let refund: string = '';

    if (originalSale.fiscal_shift_nr && originalSale.fiscal_receipt_nr) {
      const zzzz = originalSale.fiscal_shift_nr?.padStart(4, '0'),
        nnnn = originalSale.fiscal_receipt_nr?.padStart(4, '0'),
        [year, month, day] = originalSale.fiscal_date.split('-'),
        date = `${day}${month}${year}`,
        ssss = originalSale.fiscal_printer_id;

      refund = `
<printRecMessage operator="1" message="REFUND ${zzzz} ${nnnn} ${date} ${ssss}" messageType="4" />`;
    }

    const printout = `${header}${refund}
<beginFiscalReceipt />
${map(sale?.receipt, receiptLine).join('\n')}
${discount(sale.couponDiscount, 'Coupon discount')}
${discount(sale.overallReceiptDiscount, 'Total discount')}
${paymentMethod('cash', sale.payments?.cash)}
${paymentMethod('pin', sale.payments?.pin)}
${footer}`;

    return this.epsonFiscalPrinter.print(printout).then(
      (response) => {
        if (angular.isString(response.data)) {
          const parsedResponse = this.parser.parseFromString(
            response.data,
            'text/xml',
          );

          const fiscalReceiptNumber =
              parsedResponse.documentElement.getElementsByTagName(
                'fiscalReceiptNumber',
              )[0]?.textContent,
            fiscalShiftNumber =
              parsedResponse.documentElement.getElementsByTagName(
                'zRepNumber',
              )[0]?.textContent,
            fiscalReceiptDate =
              parsedResponse.documentElement.getElementsByTagName(
                'fiscalReceiptDate',
              )[0]?.textContent;

          let fiscalDate: string;

          if (
            fiscalReceiptDate &&
            angular.isString(fiscalReceiptDate) &&
            fiscalReceiptDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)
          ) {
            // assume d/m/yyyy format from the printer
            const [day, month, year] = fiscalReceiptDate.split('/');

            fiscalDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
              2,
              '0',
            )}`;
          } else {
            const now = this.dateSource.now();

            fiscalDate = now.toISOString().substr(0, 10);
          }

          if (fiscalReceiptNumber) {
            return { fiscalReceiptNumber, fiscalShiftNumber, fiscalDate };
          }

          return this.processErrorXml(parsedResponse);
        }

        return this.processErrorString(response.data);
      },
      () => {
        return this.$q.reject({
          message: this.$translate.instant(
            'shared.fiscal_printer_request_failed',
          ),
        });
      },
    );
  }

  /**
   * Reprint last receipt
   * @return resolve to true if succesfull.
   */
  lastReceipt(): ng.IPromise<any> {
    return this.epsonFiscalPrinter.print(`<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerCommand>
<printDuplicateReceipt />
</printerCommand>
</s:Body>
</s:Envelope>`);
  }

  /**
   * Login to the printer
   * @return resolve to true if succesfull.
   */
  login(password: string = '12345'): ng.IPromise<any> {
    return this.epsonFiscalPrinter
      .print(
        `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerCommand>
<directIO command="4038" data="02${password}                                                                                               "
comment="Login password followed by 95 spaces for a length of 100" />
</printerCommand>
</s:Body>
</s:Envelope>`,
      )
      .then((response) => {
        const parsedResponse = this.parser.parseFromString(
          response.data,
          'text/xml',
        );

        const tag =
          parsedResponse.documentElement.getElementsByTagName('response');

        if (tag[0].getAttribute('success') === 'true') {
          return this.$q.when('Successful login');
        }

        return this.$q.reject('Login failed');
      });
  }

  /**
   * Reset the printer
   * @return resolve to true if succesfull.
   */
  reset(): ng.IPromise<any> {
    return this.epsonFiscalPrinter
      .print(
        `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerCommand>
  <resetPrinter operator="" />
</printerCommand>
</s:Body>
</s:Envelope>`,
      )
      .then((response) => {
        const parsedResponse = this.parser.parseFromString(
          response.data,
          'text/xml',
        );

        const tag =
          parsedResponse.documentElement.getElementsByTagName('response');

        if (tag[0].getAttribute('success') === 'true') {
          return this.$q.when('Successful reset');
        }

        return this.$q.reject('Reset failed');
      });
  }

  /**
   * Reprint
   * @param arg
   * @return resolve to true if succesfull.
   */
  reprint(sale: SaleModel): ng.IPromise<any> {
    let createdDate: Date;

    if (sale.created?.date) {
      createdDate = new Date(sale.created.date);
    } else {
      createdDate = this.dateSource.now();
    }

    const day = createdDate.getDate(),
      month = createdDate.getMonth() + 1,
      year = createdDate.getFullYear();

    if (!sale.fiscal_receipt_nr) {
      return this.$q.reject(
        'Cannot reprint transaction without fiscal_receipt_nr.',
      );
    }

    return this.epsonFiscalPrinter
      .print(
        `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
<s:Body>
<printerCommand>
<printContentByNumbers dataType="0" day="${day}" month="${month}" year="${year}" fromNumber="${sale.fiscal_receipt_nr}"
toNumber="${sale.fiscal_receipt_nr}" />
</printerCommand>
</s:Body>
</s:Envelope>`,
      )
      .then((response) => {
        const parsedResponse = this.parser.parseFromString(
          response.data,
          'text/xml',
        );

        const tag =
          parsedResponse.documentElement.getElementsByTagName('response');

        if (tag[0].getAttribute('success') === 'true') {
          return this.$q.when('Successful reprint');
        }

        return this.$q.reject('Reprint failed');
      });
  }
}
